import { useEffect, useState } from "react";

import { Helmet } from "react-helmet";
import { Form, Formik } from "formik";
import { motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { isValidPhoneNumber } from "react-phone-number-input";
import { HelpCircle, Lock, Mail, Shield, Users } from "lucide-react";

import { useContactPopover } from "../../common/useContactPopover";
import { Input } from "../../common/customDesignedComponents/input";
import { Label } from "../../common/customDesignedComponents/label";
import bbbBadge from "../../../assets/new_ui_images/bbb-badge.webp";
import { Button } from "../../common/customDesignedComponents/button";
import CustomPhoneNumberInput from "../../common/CustomPhoneNumberInput";
import soc2Badge from "../../../assets/new_ui_images/soc2-type2-badge.png";
import { FormInput } from "../../common/customDesignedComponents/FormInput";
import { getObjLength, scrollToFirstFormikError } from "../../helpers/utils";
import { userSigupAction } from "../../../redux/slices/user/userSignUpSlice";
import HubspotCookieBreakerInput from "../../common/HubspotCookieBreakerInput";
import StateDropdown from "../../common/customDesignedComponents/StateDropdown";
import { TAILWIND_GRADIENT_SUBMIT_BUTTON_CLASS } from "../../helpers/constants";
import oneMillionBadge from "../../../assets/new_ui_images/one-million-usd.webp";
import StatusModal, {
  ErrorModal,
} from "../../common/customDesignedComponents/StatusModal";

export const relationships = [
  "Spouse",
  "Child",
  "Parent",
  "Sibling",
  "Grandchild",
  "Grandparent",
  "Friend",
  "Business Partner",
  "Attorney",
  "Other",
];

// Transform relationships array to StateDropdown format
const relationshipOptions = relationships.map((rel) => ({
  name: rel,
  value: rel,
  label: rel,
}));

interface BeneficiaryV2Props {
  initialValues: {
    first_name: string;
    last_name: string;
    relation: string;
    phone?: string;
    email?: string;
  };
  validationSchema: any;
  onSubmit: (values: any) => void;
  handleNextBtn: () => void;
  handleSkip: () => void;
  userEmail: string;
  isServiceProvider?: boolean;
  /** Max beneficiaries this screen allows (public signup passes 1). */
  maxBeneficiaries?: number;
}

const isOptionalPhoneValid = (value?: string | null) => {
  if (!value || String(value).trim().length === 0) {
    return true;
  }
  const phone = String(value).trim();
  return (
    isValidPhoneNumber(phone) ||
    isValidPhoneNumber(phone, "US") ||
    isValidPhoneNumber(phone, "IN")
  );
};

const BeneficiaryV2 = ({
  initialValues,
  validationSchema,
  onSubmit,
  handleNextBtn,
  handleSkip,
  userEmail = undefined,
  isServiceProvider = false,
  maxBeneficiaries = 2,
}: BeneficiaryV2Props) => {
  const dispatch = useDispatch();
  const { handleClick, ContactPopover } = useContactPopover();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [addNewBeneficiary, setAddNewBeneficiary] = useState(true);
  const [isFormValidationEnabled, setIsFormValidationEnabled] = useState(true);
  const [formInstanceKey, setFormInstanceKey] = useState(0);

  const { beneficiaryDetails, beneficiaryList } = useSelector(
    (state: any) => state.signUpUser,
  );

  const handleFormSubmit = (values: any) => {
    if (!isOptionalPhoneValid(values?.phone)) {
      return;
    }
    onSubmit(values);
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    dispatch(userSigupAction.clearBeneficiaryDetails());
    handleNextBtn();
  };

  const closeErrorModal = () => {
    dispatch(userSigupAction.clearBeneficiaryDetails());
    setShowSuccessModal(false);
    setShowErrorModal(false);
  };

  useEffect(() => {
    if (!getObjLength(beneficiaryDetails)) return;

    if (beneficiaryDetails?.success) {
      setShowErrorModal(false);
      // setShowSuccessModal(true);
      // After a successful save, return to the list view so user can add the 2nd beneficiary.
      setAddNewBeneficiary(false);
      return;
    }

    // If request failed, show error modal with best-available message
    const msg =
      beneficiaryDetails?.data?.message ||
      beneficiaryDetails?.message ||
      beneficiaryDetails?.error ||
      "We couldn't save your beneficiary details. Please try again.";

    setErrorMessage(String(msg));
    setShowSuccessModal(false);
    setShowErrorModal(true);
  }, [beneficiaryDetails]);

  useEffect(() => {
    // If beneficiary list already exists (resume flow), show list view by default.
    if ((beneficiaryList || []).length > 0) {
      setAddNewBeneficiary(false);
    }
  }, [beneficiaryList]);

  const scrollToTop = () => {
    if (typeof window === "undefined") return;
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <Helmet>
        <title>Beneficiary | EquityProtect - Add Your Beneficiary</title>
        <meta
          name="description"
          content="Add a beneficiary to your EquityProtect account for added security."
        />
      </Helmet>

      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start max-w-6xl mx-auto adbox_wrapper">
            {/* Left Column - Info Box */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="lg:sticky lg:top-32 adbox_width"
            >
              <div className="bg-gradient-teal rounded-2xl p-8 md:p-10 text-white">
                <div className="flex items-center gap-3 mb-6">
                  <Shield className="h-10 w-10 text-white" />
                  <h2 className="text-2xl md:text-3xl font-display font-medium">
                    Beneficiary Information
                  </h2>
                </div>

                <p className="text-lg text-white/90 font-medium mb-2">
                  Why do we ask for a Beneficiary?
                </p>
                <p className="text-white/80 text-sm mb-8 leading-relaxed">
                  EquityProtect asks you to name a beneficiary so there is a
                  trusted person we can contact if you are ever unavailable or
                  in the event of an emergency. This helps ensure your property
                  protection does not lapse and that important alerts or
                  decisions can still be addressed if something happens to you.
                  Your beneficiary does not have access to your account unless
                  authorized, but serves as a backup contact to help safeguard
                  your property and equity.
                </p>

                <div className="flex items-center gap-4 pt-6 border-t border-white/20">
                  <img
                    src={oneMillionBadge}
                    alt="One Million Dollar Guarantee"
                    className="h-16 w-auto"
                  />
                  <img
                    src={bbbBadge}
                    alt="BBB Accredited Business"
                    className="h-14 w-auto"
                  />
                  <img
                    src={soc2Badge}
                    alt="SOC 2 Certified"
                    className="h-14 w-auto"
                  />
                </div>
              </div>
              {/* Video Guide — hide for service-provider client onboarding */}
              {!isServiceProvider ? (
                <div className="bg-card rounded-2xl border border-border shadow-lg p-6 mt-4">
                  <h3 className="text-center text-lg font-medium text-foreground mb-4">
                    Need help? Let us guide you through the process
                  </h3>
                  <div className="aspect-video rounded-xl overflow-hidden">
                    <iframe
                      width="100%"
                      height="100%"
                      src="https://www.youtube.com/embed/bx88srhZEb4?modestbranding=1&rel=0&showinfo=0"
                      title="Beneficiary Guide"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full"
                    />
                  </div>
                </div>
              ) : null}
            </motion.div>

            {/* Right Column - Beneficiary Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="adbox_width"
            >
              <div className="bg-card rounded-2xl border border-border shadow-lg overflow-hidden">
                {/* Secure Connection Bar */}
                <div className="bg-gradient-to-r from-secondary to-cyan-400 py-3 px-4 flex items-center justify-center gap-2">
                  <Lock className="h-4 w-4 text-white" />
                  <span className="text-white font-medium text-sm">
                    Secure connection
                  </span>
                </div>

                {/* Form */}
                <div className="p-8 md:p-10">
                  <div className="flex justify-center mb-6">
                    <div className="w-16 h-16 rounded-full bg-secondary/10 flex items-center justify-center">
                      <Users className="h-8 w-8 text-secondary" />
                    </div>
                  </div>

                  <h1 className="text-xl md:text-2xl font-display font-medium text-foreground text-center mb-3">
                    Add Your Beneficiary
                  </h1>
                  <p className="text-muted-foreground text-center mb-8">
                    Please provide the contact information for your designated
                    beneficiary.
                  </p>

                  {/* Formik Form */}
                  <Formik
                    key={`beneficiary-form-${formInstanceKey}`}
                    enableReinitialize
                    validateOnChange={isFormValidationEnabled}
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleFormSubmit}
                  >
                    {({
                      values,
                      setFieldValue,
                      setFieldTouched,
                      touched,
                      errors,
                      validateForm,
                      resetForm,
                    }) => (
                      <Form id="signup-beneficiary-form">
                        {/* Block HubSpot's browser form capture: the beneficiary's name/email/phone
                            would otherwise be merged into the owner's contact. The beneficiary is
                            sent to HubSpot by the backend instead. */}
                        <HubspotCookieBreakerInput isNonHubSpotForm />

                        {!addNewBeneficiary ? (
                          <>
                            {/* Beneficiary list */}
                            {(beneficiaryList || []).length > 0 ? (
                              <div className="mb-8">
                                <div className="flex items-center justify-between mb-3">
                                  <p className="text-sm font-medium text-foreground">
                                    Your beneficiaries
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {
                                      (beneficiaryList || []).slice(
                                        0,
                                        maxBeneficiaries,
                                      ).length
                                    }
                                    /{maxBeneficiaries}
                                  </p>
                                </div>

                                <div className="space-y-3">
                                  {(beneficiaryList || [])
                                    .slice(0, maxBeneficiaries)
                                    .map((b, i) => {
                                      const first = (
                                        b?.first_name || ""
                                      ).trim();
                                      const last = (b?.last_name || "").trim();
                                      const initials =
                                        `${first?.[0] || ""}${last?.[0] || ""}`
                                          .toUpperCase()
                                          .trim();
                                      const relation =
                                        b?.relation || b?.relation_type || "-";

                                      return (
                                        <div
                                          key={b?.id ?? `benef-${i}`}
                                          className="border border-border/60 rounded-xl p-4 bg-card/40 hover:bg-card/70 transition-colors"
                                        >
                                          <div className="flex items-center gap-3">
                                            <div className="h-10 w-10 rounded-full bg-secondary/15 border border-secondary/25 flex items-center justify-center flex-shrink-0">
                                              <span className="text-sm font-semibold text-secondary">
                                                {initials || "B"}
                                              </span>
                                            </div>

                                            <div className="min-w-0 flex-1">
                                              <p className="text-sm font-semibold text-foreground truncate">
                                                {first || last
                                                  ? `${first} ${last}`.trim()
                                                  : "Beneficiary"}
                                              </p>
                                              <div className="mt-1">
                                                <span className="inline-flex items-center rounded-full border border-border bg-muted/40 px-2 py-0.5 text-xs text-muted-foreground">
                                                  {relation}
                                                </span>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                </div>
                              </div>
                            ) : (
                              <div className="mb-8 border border-dashed border-border rounded-xl p-6 bg-muted/20 text-center">
                                <p className="text-sm font-medium text-foreground">
                                  No beneficiary added yet
                                </p>
                                <p className="text-sm text-muted-foreground mt-1">
                                  {maxBeneficiaries === 1
                                    ? "Add a trusted contact for added peace of mind."
                                    : "Add up to two trusted contacts for added peace of mind."}
                                </p>
                              </div>
                            )}

                            <div className="flex justify-center gap-2">
                              {/* {(beneficiaryList || []).length < 2 ? (
                                <Button
                                  variant="outline"
                                  type="button"
                                  className="px-8 py-2.5 rounded-full font-medium text-sm w-full sm:w-auto"
                                  onClick={() => {
                                    scrollToTop();
                                    handleSkip();
                                  }}
                                >
                                  SKIP FOR NOW
                                </Button>
                              ) : null} */}

                              {(beneficiaryList || []).length <
                              maxBeneficiaries ? (
                                <Button
                                  variant="outline"
                                  type="button"
                                  className="px-8 py-2.5 rounded-full font-medium text-sm w-full sm:w-auto"
                                  onClick={() => {
                                    scrollToTop();
                                    setIsFormValidationEnabled(false);
                                    setFormInstanceKey((prev) => prev + 1);
                                    resetForm();
                                    setAddNewBeneficiary(true);
                                  }}
                                >
                                  ADD
                                </Button>
                              ) : null}

                              <Button
                                type="button"
                                onClick={() => setShowSuccessModal(true)}
                                disabled={(beneficiaryList || []).length === 0}
                                className={
                                  TAILWIND_GRADIENT_SUBMIT_BUTTON_CLASS
                                }
                              >
                                NEXT
                              </Button>
                            </div>
                          </>
                        ) : (
                          <>
                            {/* Form Fields */}
                            <div className="space-y-4 mb-8 text-left">
                              <div className="space-y-2 text-left">
                                <Label
                                  htmlFor="first_name"
                                  className="text-muted-foreground text-sm text-left block"
                                >
                                  First Name
                                </Label>
                                <Input
                                  id="firstname"
                                  name="first_name"
                                  value={values.first_name}
                                  onChange={(e) =>
                                    setFieldValue("first_name", e.target.value)
                                  }
                                  placeholder="Enter first name"
                                  className={`h-12 ${
                                    touched.first_name && errors.first_name
                                      ? "border-destructive"
                                      : ""
                                  }`}
                                />
                                {touched.first_name && errors.first_name && (
                                  <p className="text-destructive text-xs mt-1 text-left">
                                    {String(errors.first_name)}
                                  </p>
                                )}
                              </div>

                              <div className="space-y-2 text-left">
                                <Label
                                  htmlFor="last_name"
                                  className="text-muted-foreground text-sm text-left block"
                                >
                                  Last Name
                                </Label>
                                <Input
                                  id="lastname"
                                  name="last_name"
                                  value={values.last_name}
                                  onChange={(e) =>
                                    setFieldValue("last_name", e.target.value)
                                  }
                                  placeholder="Enter last name"
                                  className={`h-12 ${
                                    touched.last_name && errors.last_name
                                      ? "border-destructive"
                                      : ""
                                  }`}
                                />
                                {touched.last_name && errors.last_name && (
                                  <p className="text-destructive text-xs mt-1 text-left">
                                    {String(errors.last_name)}
                                  </p>
                                )}
                              </div>

                              <StateDropdown
                                stateList={relationshipOptions}
                                value={values.relation}
                                onChange={(value) =>
                                  setFieldValue("relation", value)
                                }
                                name="relation"
                                label="Relationship"
                                placeholder="Select relationship"
                                touched={Boolean(touched.relation)}
                                error={
                                  touched.relation && errors.relation
                                    ? String(errors.relation)
                                    : undefined
                                }
                                className=""
                                labelClassName="text-muted-foreground text-sm text-left"
                              />
                              {/* Contact Information */}
                              <div className="pt-4 border-t border-border text-left">
                                <Label className="text-muted-foreground text-sm mb-3 block text-left">
                                  Contact Information
                                </Label>
                                <div className="space-y-4">
                                  <div className="space-y-2 text-left">
                                    <Label
                                      htmlFor="phone"
                                      className="text-muted-foreground text-sm text-left block"
                                    >
                                      Mobile Phone
                                    </Label>
                                    <div className="tailwind-phone-input">
                                      <div className="relative form-input-wrapper">
                                        <div className="phone-input-container">
                                          <CustomPhoneNumberInput
                                            name="phone"
                                            values={values}
                                            touched={touched}
                                            setFieldValue={setFieldValue}
                                            errors={errors}
                                            placeholder="Mobile Phone"
                                            showError={false}
                                            errorOnlyWhenTouched
                                            otherProps={{
                                              id: "phonenumber",
                                              onBlur: () => {
                                                setFieldTouched("phone", true);
                                              },
                                              onChange: (event?: string) => {
                                                setFieldValue(
                                                  "phone",
                                                  event || "",
                                                );
                                              },
                                            }}
                                          />
                                        </div>
                                      </div>
                                      {touched.phone &&
                                        ((errors.phone &&
                                          String(errors.phone)) ||
                                          (values?.phone &&
                                            !isOptionalPhoneValid(
                                              values?.phone,
                                            ))) && (
                                          <p className="text-sm text-red-500 mt-1 text-left tailwind_error_msg">
                                            {touched.phone && errors.phone
                                              ? String(errors.phone)
                                              : "Please enter a valid phone number"}
                                          </p>
                                        )}
                                    </div>
                                  </div>
                                  <div className="space-y-2 text-left">
                                    <Label
                                      htmlFor="email"
                                      className="text-muted-foreground text-sm flex items-center gap-2 text-left"
                                    >
                                      <Mail className="h-4 w-4" />
                                      Email Address
                                    </Label>
                                    <FormInput
                                      id="emailId"
                                      name="email"
                                      type="email"
                                      placeholder="Enter email address"
                                      showError={false}
                                      className={`h-12 ${
                                        touched.email && errors.email
                                          ? "border-destructive"
                                          : ""
                                      }`}
                                      validation={{
                                        values,
                                        setFieldValue,
                                        setFieldTouched,
                                        touched,
                                        errors,
                                      }}
                                    />
                                    {touched.email && errors.email && (
                                      <p className="text-destructive text-xs mt-1 text-left">
                                        {String(errors.email)}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="flex justify-center gap-2">
                              {(beneficiaryList || []).length < 1 ? (
                                <Button
                                  variant="outline"
                                  type="button"
                                  className="px-8 py-2.5 rounded-full font-medium text-sm w-full sm:w-auto"
                                  onClick={() => {
                                    scrollToTop();
                                    handleSkip();
                                  }}
                                >
                                  SKIP FOR NOW
                                </Button>
                              ) : null}
                              {(beneficiaryList || []).length > 0 ? (
                                <Button
                                  variant="outline"
                                  type="button"
                                  className="px-8 py-2.5 rounded-full font-medium text-sm w-full sm:w-auto"
                                  onClick={() => {
                                    scrollToTop();
                                    resetForm();
                                    setAddNewBeneficiary(false);
                                  }}
                                >
                                  CANCEL
                                </Button>
                              ) : null}
                              <Button
                                type="submit"
                                onClick={async (e) => {
                                  scrollToTop();
                                  setIsFormValidationEnabled(true);
                                  const formErrors = await validateForm();
                                  const firstErrorPath =
                                    scrollToFirstFormikError({
                                      errors: formErrors,
                                      setFieldTouched,
                                    });
                                  if (firstErrorPath) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }
                                }}
                                className={
                                  TAILWIND_GRADIENT_SUBMIT_BUTTON_CLASS
                                }
                              >
                                SAVE
                              </Button>
                            </div>
                          </>
                        )}
                      </Form>
                    )}
                  </Formik>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Error Modal (Reusable StatusModal) */}
      <ErrorModal
        open={showErrorModal}
        onOpenChange={setShowErrorModal}
        title="Unable to save beneficiary"
        message={
          <p className="text-muted-foreground text-center">{errorMessage}</p>
        }
        primaryButtonLabel="OK"
        onPrimaryAction={() => closeErrorModal()}
        footer={
          <p className="text-muted-foreground text-sm flex items-center justify-center gap-2">
            <HelpCircle className="w-4 h-4" />
            Have questions?{" "}
            <button
              type="button"
              onClick={handleClick}
              className="text-secondary hover:text-secondary/80 font-medium bg-transparent border-0 p-0 cursor-pointer"
            >
              Contact support
            </button>
          </p>
        }
      />

      {/* Success Modal (Reusable StatusModal) */}
      <StatusModal
        open={showSuccessModal}
        onOpenChange={setShowSuccessModal}
        variant="success"
        title="Congratulations!"
        forcePrimaryActionOnly
        message={[
          // {
          //   content: (
          //     <p className="text-foreground">
          //       Your property located at{" "}
          //       <span className="font-semibold text-secondary">
          //         {propertyAddress}
          //       </span>{" "}
          //       is now added to the EquityProtect System.
          //     </p>
          //   ),
          // },
          {
            heading: "So what's next?",
            content: (
              <>
                <p>
                  Next, EquityProtect will prepare and record the lock on the
                  property that only you can unlock. In some counties a local
                  title or legal partner may prepare and record that filing on
                  EquityProtect&apos;s behalf. Partners do not receive access to
                  your payment methods.
                </p>
                <p>
                  You will receive a copy of this document back in your
                  dashboard within the next couple of weeks depending on your
                  County Recorder. Prefer EquityProtect staff to handle
                  recording instead of a local partner? Set the UCC recording
                  preference on your Properties page after signup.
                </p>
                <p>
                  Thank you for entrusting your property and equity to
                  EquityProtect.
                </p>
              </>
            ),
          },
        ]}
        primaryButtonLabel="NEXT"
        onPrimaryAction={handleCloseSuccessModal}
        footer={
          <p className="text-muted-foreground text-sm flex items-center justify-center gap-2">
            <HelpCircle className="w-4 h-4" />
            Have questions?{" "}
            <button
              type="button"
              onClick={handleClick}
              className="text-secondary hover:text-secondary/80 font-medium bg-transparent border-0 p-0 cursor-pointer"
            >
              Contact support
            </button>
          </p>
        }
      />
      <ContactPopover />
    </>
  );
};

export default BeneficiaryV2;
